/* global newsletterConfig */
import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { __ } from '@wordpress/i18n';

// Services.
import getApiFetch from '../../js/services/getApiFetch';

const appElement = document.querySelector('.newsletter__form');
const apiFetch = getApiFetch();

/**
 * Component for expanded newsletter form.
 */
export default class Newsletter extends React.Component {
  static propTypes = {
    type: PropTypes.string.isRequired,
    fields: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      required: PropTypes.bool,
    })).isRequired,
    topic: PropTypes.shape({
      categoryId: PropTypes.string,
      id: PropTypes.string,
      listId: PropTypes.string,
      name: PropTypes.string,
    }),
    interests: PropTypes.arrayOf(PropTypes.shape({})),
  };

  static defaultProps = {
    topic: null,
    interests: null,
  };

  /**
   *
   * Renders a single checkbox input with label
   * @static
   * @param {*} interest
   * @param {*} onChange
   * @param {*} classNames
   * @returns
   * @memberof Newsletter
   */
  static renderInterestCheckBox(interest, onChange, classNames = '') {
    return (
      <div className={classNames} key={interest.id}>
        <input
          className="newsletter__checkbox"
          type="checkbox"
          checked={interest.selected}
          onChange={onChange}
          name={interest.name}
          value={interest.id}
          id={interest.id}
        />
        <label htmlFor={interest.id}>
          <span>{interest.name}</span>
        </label>
      </div>
    );
  }

  /**
   * Start the component
   */
  constructor(props) {
    super(props);
    const {
      fields,
      topic,
      interests,
    } = props;

    this.state = {
      fields: fields.map((field) => ({ ...field, value: '' })),
      topic: { ...topic, selected: true },
      interests: interests
        .map((interest) => ({ ...interest, selected: false })),
      formValidation: {
        valid: false,
        shouldValidate: true,
        errorMessages: [],
      },
      apiStatus: {
        statusText: '',
        status: '',
      },
      loading: false,
    };

    this.toggleTopicCheckBox = this.toggleTopicCheckBox.bind(this);
    this.toggleInterestCheckBox = this.toggleInterestCheckBox.bind(this);
    this.validateNewsletterForm = this.validateNewsletterForm.bind(this);
    this.newsletterFieldChange = this.newsletterFieldChange.bind(this);
    this.submitSubscription = this.submitSubscription.bind(this);
    this.renderFullTopicsList = this.renderFullTopicsList.bind(this);
  }

  toggleTopicCheckBox() {
    const { topic } = this.state;
    this.setState({ topic: { ...topic, selected: ! topic.selected } });
  }

  toggleInterestCheckBox(interest) {
    const { interests } = this.state;
    this.setState({
      interests: interests.reduce((acc, curr) => [
        ...acc,
        (curr.id === interest.id ? {
          ...interest, selected: ! interest.selected,
        } : curr),
      ], []),
    });
  }

  newsletterFieldChange(event, field) {
    const { fields } = this.state;
    let targetValue = event.target.value;
    if ('consent' === field.name) {
      targetValue = event.target.checked;
    }
    this.setState({
      fields: fields.reduce((acc, curr) => [
        ...acc,
        (curr.name === field.name ? {
          ...field, value: targetValue,
        } : curr),
      ], []),
      formValidation: {
        ...this.state.formValidation, shouldValidate: true,
      },
    });
  }

  validateNewsletterForm(event) {
    event.preventDefault();
    const {
      fields,
      formValidation: { shouldValidate, valid },
    } = this.state;

    // No need to validate right now - nothing's changed.
    if (! shouldValidate && ! valid) {
      return;
    }

    const errorMessages = fields.reduce((acc, curr) => {
      if (curr.required) {
        if (
          'email' === curr.name
          && ! curr.value.trim()
        ) {
          acc.push('Required fields missing');
        }

        if (
          'consent' === curr.name && (
            '' === curr.value
            || ! curr.value
          )
        ) {
          acc.push('Required fields missing');
        }
      }

      // Regex is the same as what an HTML5 email input uses for valid emails
      if (
        'email' === curr.name &&
        curr.value.trim() &&
        ! /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(curr.value) // eslint-disable-line max-len
      ) {
        acc.push(__('Email is invalid', 'knight-foundation-app'));
      }
      return acc;
    }, []);

    this.setState({
      loading: 0 === errorMessages.length,
      formValidation: {
        valid: 0 === errorMessages.length,
        shouldValidate: false,
        errorMessages: 1 === errorMessages.length ? errorMessages :
          [...new Set([...errorMessages])],
      },
    }, this.submitSubscription);
  }

  submitSubscription() {
    const {
      formValidation,
      fields,
      topic,
      interests,
    } = this.state;
    const { type } = this.props;

    if (! formValidation.valid) {
      return;
    }

    // Build POST data
    const data = fields.reduce((acc, curr) => {
      switch (curr.name) {
        case 'email':
          return { ...acc, email_address: curr.value };
        case 'fname':
          return {
            ...acc,
            merge_fields: {
              FNAME: curr.value,
              ...acc.merge_fields,
            },
          };
        case 'lname':
          return {
            ...acc,
            merge_fields: {
              LNAME: curr.value,
              ...acc.merge_fields,
            },
          };
        default:
          return acc;
      }
    }, {
      // Send in blank merge_fields in case block type doesn't have these fields
      merge_fields: {
        FNAME: 'Email',
        LNAME: 'Subscriber',
      },
    });

    // Do not send in interests if in compact view
    if ('compact' !== type) {
      data.interests = [...interests, topic.id ? topic : {}]
        .reduce((acc, curr) => ({ ...acc, [curr.id]: curr.selected }), {});
    }

    data.status = 'subscribed';

    apiFetch({
      path: '/knight-foundation-app/v1/add-mailchimp-subscription',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
      .then((response) => this.setState({
        apiStatus: response,
        loading: false,
      }));
  }

  renderFullTopicsList() {
    const { type } = this.props;
    const { interests } = this.state;
    if (
      ('topic' === type || 'compact' === type)
      || 0 === interests.length
    ) {
      return null;
    }

    return (
      <div className="signup__row">
        <p className="newsletter__interests-text">
          {__(
            'Please tell us more about your interests:',
            'knight-foundation-app'
          )}
        </p>
        {
          interests && 'full' === type && interests.map((interest) =>
            Newsletter.renderInterestCheckBox(
              interest,
              () => this.toggleInterestCheckBox(interest),
              'signup__item signup__item--type--half'
            ))
        }
      </div>
    );
  }

  renderSubscribeStatus() {
    const {
      apiStatus,
    } = this.state;

    return (
      <div>
        {
          apiStatus.statusText && (
            <div
              className={[
                'newsletter__status',
                `newsletter__status${apiStatus.status}`,
              ].join(' ')}
            >
              <p>
                {__(apiStatus.statusText, 'knight-foundation-app')}
              </p>
            </div>
          )
        }
      </div>
    );
  }

  renderValidationErrors() {
    const {
      formValidation: { valid, errorMessages },
    } = this.state;

    return (
      <div>
        {
          ! valid && errorMessages.map((error) => (
            <p className="newsletter__form-error">{error}</p>
          ))
        }
      </div>
    );
  }

  render() {
    const {
      fields,
      topic,
      loading,
    } = this.state;

    const { type } = this.props;

    const anyRequired = !! fields.filter((field) => field.required).shift();

    return (
      <div className="newsletter__inner">
        {'full' !== type && this.renderSubscribeStatus()}
        {this.renderValidationErrors()}
        {
          anyRequired && (
            <div className="newsletter__indicates-required">
              <span className="newsletter__asterisk">*&nbsp;</span>
              {__('indicates required', 'knight-foundation-app')}
            </div>
          )
        }
        {
          fields.map((field) => (
            <label htmlFor={field.name} key={field.name}>
              <span>
                {field.label}&nbsp;&nbsp;
                {
                  field.required &&
                  <span className="newsletter__asterisk">*</span>
                }
              </span>
              <input
                className="newsletter__field"
                type={'consent' === field.name ? 'checkbox' : field.name}
                id={field.name}
                name={field.name}
                value={field.value}
                onChange={(event) => this
                  .newsletterFieldChange(event, field)}
              />
            </label>
          ))
        }
        <div className="newsletter__topic-list">
          {
            topic.id && Newsletter.renderInterestCheckBox(
              topic,
              this.toggleTopicCheckBox
            )
          }
          {this.renderFullTopicsList()}
        </div>
        {'full' === type && this.renderSubscribeStatus()}
        {'full' === type && this.renderValidationErrors()}
        <button
          className="newsletter__button"
          type="submit"
          onClick={this.validateNewsletterForm}
        >
          {__(loading ? 'Please Wait...' : 'Sign Up', 'knight-foundation-app')}
        </button>
      </div>
    );
  }
}

if (appElement) {
  // create Newsletter component with config from:
  // client-mu-plugins/knight-foundation-app/template-parts/blocks/newsletter.php
  ReactDOM.render(
    React.createElement(Newsletter, newsletterConfig || {}),
    appElement
  );
}
